import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { flattenMetricsList, mergeSparklineData, getDateRangeSubtitle, IDateSubtitleConfig } from './utils';

interface IDataMergeWrapProps {
  data: Record<string, unknown>;
  dataKey: string;
  sparklineDataKey?: string;
  studioIconMap?: Record<string, string>;
  dateSubtitleConfig?: IDateSubtitleConfig;
  children: React.ReactElement | React.ReactElement[];
  [key: string]: unknown;
}

const DataMergeWrap: React.FC<IDataMergeWrapProps> = (props) => {
  const { data: propsData, dataKey, sparklineDataKey, studioIconMap, dateSubtitleConfig, children, ...restProps } = props;
  const { locale } = useIntl();
  const isEN = locale === 'en-US';

  const serviceData = propsData?.[dataKey] as Record<string, unknown> | undefined;
  const casualInfo = propsData?.casualInfo as Record<string, unknown> | undefined;
  const sparklineServiceData = sparklineDataKey
    ? (propsData?.[sparklineDataKey] as Record<string, unknown> | undefined)
    : undefined;

  const extraParams = propsData?.extraParams as Record<string, unknown> | undefined;

  const transformedData = useMemo(() => {
    const apiData = serviceData?.data as Record<string, unknown> | undefined;
    if (!apiData?.metrics_list) return serviceData;

    const page = (extraParams?.page as number) || 1;
    const pageSize = (extraParams?.page_size as number) || 10;
    const pageOffset = (page - 1) * pageSize;

    const games = casualInfo?.games as Array<{ game_name: string; cover?: string; country_abbre?: string; country_name_en?: string; [key: string]: string | undefined; }> | undefined;
    const metricsList = apiData.metrics_list as Array<Record<string, unknown>>;
    let flatList = flattenMetricsList(metricsList, games, studioIconMap, pageOffset, isEN);

    if (sparklineServiceData) {
      const sparklineApiData = sparklineServiceData.data as Record<string, unknown> | undefined;
      const sparklineMetrics = sparklineApiData?.metrics_list as Array<Record<string, unknown>> | undefined;
      if (sparklineMetrics?.length) {
        flatList = mergeSparklineData(flatList, sparklineMetrics);
      }
    }

    return {
      ...serviceData,
      data: {
        ...apiData,
        metrics_list: flatList,
      },
    };
  }, [serviceData, casualInfo, sparklineServiceData, studioIconMap, extraParams, isEN]);

  const dateRangeSubtitle = useMemo(() => {
    const apiData = serviceData?.data as Record<string, unknown> | undefined;
    const metricsList = apiData?.metrics_list as unknown[] | undefined;
    if (!metricsList?.length) return { en: '', cn: '' };

    const filter = propsData?.filter as
      | { start_time?: string; end_time?: string; }
      | undefined;
    const endTimeFromData = dateSubtitleConfig?.subtitleEndTimeDataKey
      ? (propsData?.[dateSubtitleConfig.subtitleEndTimeDataKey] as string | undefined)
      : undefined;
    const endTime = dateSubtitleConfig?.subtitleEndTimeDataKey
      ? endTimeFromData
      : filter?.end_time;
    return getDateRangeSubtitle(filter?.start_time, endTime, dateSubtitleConfig);
  }, [propsData?.filter, propsData, dateSubtitleConfig, serviceData]);

  const childData = useMemo(() => {
    return {
      ...propsData,
      [dataKey]: transformedData,
      __dateRangeSubtitleEn__: dateRangeSubtitle.en,
      __dateRangeSubtitleCn__: dateRangeSubtitle.cn,
    };
  }, [propsData, dataKey, transformedData, dateRangeSubtitle]);

  return (
    <>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          ...restProps,
          data: childData,
        }),
      )}
    </>
  );
};

export default DataMergeWrap;
